import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
    Grid, Card, Checkbox, ListItemText, IconButton, Icon,
    Drawer, List, ListItem, ListItemButton, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress, InputLabel, TextField, FormControlLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import { MDTextFieldCalendar } from "components/MDTextFiedCalendar";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import PieChart from "examples/Charts/PieChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
import { get, post, postNoResponse, postFile } from "utils/WebService";
import { errorAlert, successAlert } from "utils/Functions";
import { useMaterialUIController } from "context";

const columnsByFonte = {
    acordos: [
        { value: "ID Acordo", label: "ID Acordo" },
        { value: "Devedor Nome", label: "Devedor Nome" },
        { value: "Devedor Documento", label: "Devedor Documento" },
        { value: "Cliente Nome", label: "Cliente Nome" },
        { value: "Operador Acordo", label: "Operador Acordo" },
        { value: "Campanha", label: "Campanha" },
        { value: "Data Formalizacao", label: "Data Formalização" },
        { value: "Valor Original", label: "Valor Original" },
        { value: "Valor Acordo", label: "Valor Acordo" },
        { value: "Desconto", label: "Desconto" },
        { value: "Qtd Parcelas", label: "Qtd Parcelas" },
        { value: "Forma Pagamento", label: "Forma Pagamento" },
        { value: "Situacao Acordo", label: "Situação Acordo" },
        { value: "Num Parcela", label: "Num Parcela" },
        { value: "Valor Parcela", label: "Valor Parcela" },
        { value: "Vencimento Parcela", label: "Vencimento Parcela" },
        { value: "Situacao Parcela", label: "Situação Parcela" },
        { value: "Data Pagamento Parcela", label: "Data Pagamento Parcela" },
        { value: "Valor Pago Parcela", label: "Valor Pago Parcela" },
        { value: "Data Baixa Parcela", label: "Data Baixa Parcela" },
        { value: "Operador Baixa Parcela", label: "Operador Baixa Parcela" },
        { value: "Desconto Baixa Parcela", label: "Desconto Baixa Parcela" },
        { value: "Acrescimo Baixa Parcela", label: "Acréscimo Baixa Parcela" },
        { value: "Forma Pagamento Parcela", label: "Forma Pagamento Parcela" }
    ],
    devedores: [
        { value: "ID Devedor", label: "ID Devedor" },
        { value: "Devedor Nome", label: "Devedor Nome" },
        { value: "Devedor Documento", label: "Devedor Documento" },
        { value: "Cliente Nome", label: "Cliente Nome" },
        { value: "Codigo Cliente", label: "Código Cliente" },
        { value: "Carteira", label: "Carteira" },
        { value: "Status Ficha", label: "Status Ficha" },
        { value: "Operador Ficha", label: "Operador Ficha" },
        { value: "Data Distribuicao", label: "Data Distribuição" },
        { value: "Situacao Ficha", label: "Situação Ficha" }
    ],
    ocorrencias: [
        { value: "ID Ocorrencia", label: "ID Ocorrência" },
        { value: "Devedor Nome", label: "Devedor Nome" },
        { value: "Devedor Documento", label: "Devedor Documento" },
        { value: "Cliente Nome", label: "Cliente Nome" },
        { value: "Operador Ocorrencia", label: "Operador Ocorrência" },
        { value: "Tipo Ocorrencia", label: "Tipo Ocorrência" },
        { value: "Data Ocorrencia", label: "Data Ocorrência" },
        { value: "Observacao", label: "Observação" }
    ],
    contratos: [
        { value: "ID Contrato", label: "ID Contrato" },
        { value: "Titulo", label: "Título" },
        { value: "Parcela", label: "Parcela" },
        { value: "Descricao", label: "Descrição" },
        { value: "Devedor Nome", label: "Devedor Nome" },
        { value: "Devedor Documento", label: "Devedor Documento" },
        { value: "Cliente Nome", label: "Cliente Nome" },
        { value: "Carteira", label: "Carteira" },
        { value: "Valor Original", label: "Valor Original" },
        { value: "Saldo", label: "Saldo" },
        { value: "Vencimento", label: "Vencimento" },
        { value: "Emissao", label: "Data Emissão" },
        { value: "Criacao", label: "Data Cadastro" },
        { value: "Devolucao", label: "Data Devolução" },
        { value: "Situacao Contrato", label: "Situação Contrato" }
    ],
    boletos: [
        { value: "ID Boleto", label: "ID Boleto" },
        { value: "Nosso Numero", label: "Nosso Número" },
        { value: "DV Nosso Numero", label: "DV Nosso Número" },
        { value: "Devedor Nome", label: "Devedor Nome" },
        { value: "Devedor Documento", label: "Devedor Documento" },
        { value: "Cliente Nome", label: "Cliente Nome" },
        { value: "Valor Boleto", label: "Valor Boleto" },
        { value: "Valor Pago", label: "Valor Pago" },
        { value: "Vencimento", label: "Vencimento" },
        { value: "Pagamento", label: "Data Pagamento" },
        { value: "Emissao", label: "Data Emissão" },
        { value: "Linha Digitavel", label: "Linha Digitável" },
        { value: "Situacao Boleto", label: "Situação Boleto" }
    ]
};

export default function ReportsSystem() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    // Filters and Formats state
    const [selectedFontes, setSelectedFontes] = useState([
        { value: "acordos", label: "Acordos Formalizados" }
    ]);
    const fonteDados = useMemo(() => selectedFontes.map(f => f.value).join(","), [selectedFontes]);

    const availableColumns = useMemo(() => {
        const cols = [];
        const seen = new Set();
        selectedFontes.forEach(f => {
            const list = columnsByFonte[f.value] || [];
            list.forEach(col => {
                if (!seen.has(col.value)) {
                    seen.add(col.value);
                    cols.push(col);
                }
            });
        });
        return cols;
    }, [selectedFontes]);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selectedOperators, setSelectedOperators] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [selectedCarteiras, setSelectedCarteiras] = useState([]);
    const [selectedSituations, setSelectedSituations] = useState([]);
    const [selectedTipoOcorrencias, setSelectedTipoOcorrencias] = useState([]);
    const [selectedClientes, setSelectedClientes] = useState([]);
    const [excetoDevolvidos, setExcetoDevolvidos] = useState(false);

    const [dateFormat, setDateFormat] = useState("dd/MM/yyyy");
    const [valueFormat, setValueFormat] = useState("R$ #,##0.00");
    const [selectedColumns, setSelectedColumns] = useState(columnsByFonte.acordos);
    const [columnAliases, setColumnAliases] = useState({});

    // Dropdown options
    const [operatorsList, setOperatorsList] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [carteirasList, setCarteirasList] = useState([]);
    const [tipoOcorrenciasList, setTipoOcorrenciasList] = useState([]);
    const [clientesList, setClientesList] = useState([]);
    const [situationsList] = useState([
        { id: 1, name: "Pendente" },
        { id: 3, name: "Pago" },
        { id: 2, name: "Quebrado" },
        { id: 4, name: "Cancelado" }
    ]);

    // Data Table state
    const [tableData, setTableData] = useState([]);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loadingTable, setLoadingTable] = useState(false);
    const [aplicar, setAplicar] = useState(false);

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: []
    });

    // Chart / Grouping state
    const [groupColumn, setGroupColumn] = useState("");
    const [aggregateColumn, setAggregateColumn] = useState("");
    const [aggregateType, setAggregateType] = useState("count");
    const [chartType, setChartType] = useState("bar");
    const [colorPalette, setColorPalette] = useState("material");

    // Saved Reports state
    const [customReportsList, setCustomReportsList] = useState([]);
    const [savedReports, setSavedReports] = useState([]);
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [newReportName, setNewReportName] = useState("");
    const [activeReport, setActiveReport] = useState(null);

    // Load initial filter options
    useEffect(() => {
        get("/users/list-all-operators")
            .then(res => setOperatorsList(res.list || res || []))
            .catch(() => setOperatorsList([]));

        get("/status-ficha/list-all")
            .then(res => setStatusList(res.list || res || []))
            .catch(() => setStatusList([]));

        get("/carteira/list-all")
            .then(res => setCarteirasList(res.list || res || []))
            .catch(() => setCarteirasList([]));

        get("/tipo-ocorrencia/list-all")
            .then(res => setTipoOcorrenciasList(res.list || res || []))
            .catch(() => setTipoOcorrenciasList([]));

        get("/pessoa/list-all")
            .then(res => setClientesList(res.list || res || []))
            .catch(() => setClientesList([]));

        get("/relatorio-customizado/ativos")
            .then(res => setCustomReportsList(res || []))
            .catch(() => setCustomReportsList([]));

        loadSavedReports();
    }, []);

    const loadSavedReports = () => {
        get("/reports/saved")
            .then(res => setSavedReports(res || []))
            .catch(() => setSavedReports([]));
    };

    // Date formatting helper
    const formatDateValue = (val, format) => {
        if (!val) return "-";
        try {
            const date = new Date(val);
            if (isNaN(date.getTime())) return val;

            const pad = (n) => n.toString().padStart(2, "0");
            const day = pad(date.getDate());
            const month = pad(date.getMonth() + 1);
            const year = date.getFullYear();
            const hours = pad(date.getHours());
            const minutes = pad(date.getMinutes());
            const seconds = pad(date.getSeconds());

            if (format === "dd/mm/yyyy hh:mm:ss") {
                return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
            } else if (format === "yyyy-mm-dd") {
                return `${year}-${month}-${day}`;
            }
            return `${day}/${month}/${year}`;
        } catch (e) {
            return val;
        }
    };

    // Currency/Number formatting helper
    const formatNumericValue = (val, format) => {
        if (val === null || val === undefined) return "-";
        if (typeof val === "number" || (typeof val === "string" && !isNaN(val) && val.trim() !== "")) {
            const num = Number(val);
            if (format === "R$ #,##0.00") {
                return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
            } else if (format === "#,##0.00") {
                return num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            } else if (format === "#,##0") {
                return num.toLocaleString("pt-BR", { maximumFractionDigits: 0 });
            }
            return num.toString();
        }
        return val;
    };

    // Determine cell content dynamically based on formats
    const renderCell = (key, val) => {
        const lowerKey = key.toLowerCase();
        if (lowerKey.includes("data") || lowerKey.includes("criacao") || lowerKey.includes("vencimento")) {
            return formatDateValue(val, dateFormat);
        }
        if (lowerKey.includes("valor") || lowerKey.includes("desconto") || lowerKey.includes("acrescimo")) {
            return formatNumericValue(val, valueFormat);
        }
        return val ?? "-";
    };

    // Build TanStack Table Column Definitions dynamically based on result keys
    const columns = useMemo(() => {
        if (tableData.length === 0) return [];
        const firstRow = tableData[0];
        return Object.keys(firstRow).map(key => ({
            header: columnAliases[key] || key,
            accessorKey: key,
            cell: info => <DefaultCell>{renderCell(key, info.getValue())}</DefaultCell>,
            isSorted: true
        }));
    }, [tableData, dateFormat, valueFormat, columnAliases]);

    // Handle pagination/sorting on client-side
    const handleFetchData = useCallback(({ pageIndex, pageSize, sorting }) => {
        const idx = (pageIndex !== undefined) ? pageIndex : tableState.current.pageIndex;
        const size = (pageSize !== undefined) ? pageSize : tableState.current.pageSize;
        const sort = (sorting !== undefined) ? sorting : tableState.current.sorting;

        tableState.current = {
            ...tableState.current,
            pageIndex: idx,
            pageSize: size,
            sorting: sort
        };

        let sorted = [...tableData];
        if (sort && sort.length > 0) {
            const { id, desc } = sort[0];
            sorted.sort((a, b) => {
                const valA = a[id] ?? "";
                const valB = b[id] ?? "";
                if (valA < valB) return desc ? 1 : -1;
                if (valA > valB) return desc ? -1 : 1;
                return 0;
            });
        }

        const start = idx * size;
        const end = start + size;
        setDisplayedData(sorted.slice(start, end));
        setTotalPages(Math.ceil(sorted.length / size));
        setTotalRecords(sorted.length);
        setAplicar(false);
    }, [tableData]);

    // Trigger local updates when master dataset changes
    useEffect(() => {
        handleFetchData({ pageIndex: 0 });
    }, [tableData, handleFetchData]);

    // Run dynamic query on server
    const handleRunReport = () => {
        if (!selectedFontes || selectedFontes.length === 0) {
            errorAlert("Por favor, selecione ao menos uma Fonte de Dados.");
            return;
        }

        setLoadingTable(true);
        const filtros = {
            startDate,
            endDate,
            userIds: selectedOperators.map(o => o.cvCodUsuario || o.id),
            statusFichaIds: selectedStatus.map(s => s.cvId || s.id),
            carteiraIds: selectedCarteiras.map(c => c.cvId || c.id),
            situations: selectedSituations.map(s => s.id),
            tipoOcorrenciaIds: selectedTipoOcorrencias.map(t => t.cvId || t.id),
            clienteIds: selectedClientes.map(c => c.id),
            excetoDevolvidos,
            selectedColumns: selectedColumns.map(c => c.value),
            columnAliases
        };

        post("/reports/run", { fonteDados, filtros })
            .then(res => {
                setTableData(res || []);
                // Reset grouping selectors to default based on datasource
                if (res && res.length > 0) {
                    const firstRow = res[0];
                    const keys = Object.keys(firstRow);
                    if (keys.includes("Situacao Acordo")) {
                        setGroupColumn("Situacao Acordo");
                        setAggregateColumn("Valor Acordo");
                        setAggregateType("sum");
                    } else if (keys.includes("Carteira")) {
                        setGroupColumn("Carteira");
                        setAggregateColumn(keys.includes("ID Devedor") ? "ID Devedor" : keys[0]);
                        setAggregateType("count");
                    } else if (keys.includes("Tipo Ocorrencia")) {
                        setGroupColumn("Tipo Ocorrencia");
                        setAggregateColumn(keys.includes("ID Ocorrencia") ? "ID Ocorrencia" : keys[0]);
                        setAggregateType("count");
                    } else {
                        setGroupColumn(keys[0] || "");
                        setAggregateColumn(keys[0] || "");
                        setAggregateType("count");
                    }
                }
                setAplicar(true);
                setLoadingTable(false);
            })
            .catch(err => {
                errorAlert("Erro ao executar busca de relatório.");
                setTableData([]);
                setLoadingTable(false);
            });
    };

    // Export formatted XLSX using backend POI
    const handleExportXlsx = () => {
        if (!selectedFontes || selectedFontes.length === 0) {
            errorAlert("Por favor, selecione ao menos uma Fonte de Dados.");
            return;
        }

        const filtros = {
            startDate,
            endDate,
            userIds: selectedOperators.map(o => o.cvCodUsuario || o.id),
            statusFichaIds: selectedStatus.map(s => s.cvId || s.id),
            carteiraIds: selectedCarteiras.map(c => c.cvId || c.id),
            situations: selectedSituations.map(s => s.id),
            tipoOcorrenciaIds: selectedTipoOcorrencias.map(t => t.cvId || t.id),
            clienteIds: selectedClientes.map(c => c.id),
            excetoDevolvidos,
            selectedColumns: selectedColumns.map(c => c.value),
            columnAliases
        };

        // Standard POI formats expect lowercase dates/BRL symbols
        const mappedDateFormat = dateFormat === "dd/MM/yyyy HH:mm:ss" ? "dd/mm/yyyy hh:mm:ss" : dateFormat.toLowerCase();
        const mappedValueFormat = valueFormat === "R$ #,##0.00" ? "\"R$\" #,##0.00" : valueFormat;
        
        let fileName;
        const currentDateStr = new Date().toISOString().slice(0, 10);
        if (activeReport && activeReport.ccNome) {
            const sanitizedName = activeReport.ccNome
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-zA-Z0-9\s-_]/g, "")
                .trim()
                .replace(/\s+/g, "_");
            fileName = `${sanitizedName}_${currentDateStr}.xlsx`;
        } else {
            fileName = `relatorio_${fonteDados.replace(/,/g, "_")}_${currentDateStr}.xlsx`;
        }

        postFile("/reports/export", {
            fonteDados,
            filtros,
            dateFormat: mappedDateFormat,
            valueFormat: mappedValueFormat,
            chartConfig: {
                groupColumn,
                aggregateColumn,
                aggregateType,
                chartType,
                colorPalette
            }
        }, fileName)
            .catch(() => errorAlert("Erro ao exportar planilha formatada."));
    };

    // Clear all filters, selected columns and aliases
    const handleClearFilters = () => {
        setStartDate("");
        setEndDate("");
        setSelectedOperators([]);
        setSelectedStatus([]);
        setSelectedCarteiras([]);
        setSelectedSituations([]);
        setSelectedTipoOcorrencias([]);
        setSelectedClientes([]);
        setExcetoDevolvidos(false);
        
        // Reset selected columns to the full set for current selected sources
        const cols = [];
        const seen = new Set();
        selectedFontes.forEach(f => {
            const list = columnsByFonte[f.value] || [];
            list.forEach(col => {
                if (!seen.has(col.value)) {
                    seen.add(col.value);
                    cols.push(col);
                }
            });
        });
        setSelectedColumns(cols);
        setColumnAliases({});
        setColorPalette("material");
        setTableData([]);
        setActiveReport(null);
        setNewReportName("");
    };

    // Save report configuration
    const handleSaveReport = (targetId = null) => {
        if (!newReportName.trim()) {
            errorAlert("Por favor informe um nome para o relatório.");
            return;
        }

        const configObj = {
            fonteDados,
            startDate,
            endDate,
            selectedOperators,
            selectedStatus,
            selectedCarteiras,
            selectedSituations,
            selectedTipoOcorrencias,
            selectedClientes,
            excetoDevolvidos,
            dateFormat,
            valueFormat,
            groupColumn,
            aggregateColumn,
            aggregateType,
            chartType,
            colorPalette,
            selectedColumns,
            columnAliases
        };

        post("/reports/saved", {
            id: targetId,
            nome: newReportName,
            fonteDados,
            configJson: JSON.stringify(configObj)
        }).then((res) => {
            successAlert("Configuração de relatório salva com sucesso!");
            setSaveDialogOpen(false);
            if (res && res.cvId) {
                setActiveReport(res);
            } else if (targetId) {
                setActiveReport(prev => prev ? { ...prev, ccNome: newReportName } : null);
            }
            loadSavedReports();
        }).catch(() => errorAlert("Erro ao salvar relatório."));
    };

    // Load saved configuration
    const handleLoadReportConfig = (report) => {
        try {
            const config = JSON.parse(report.ccConfigJson);
            const loadedFonte = config.fonteDados || "acordos";
            
            const loadedFontesList = loadedFonte.split(",").map(val => {
                const opt = [
                    { value: "acordos", label: "Acordos Formalizados" },
                    { value: "devedores", label: "Fichas / Devedores" },
                    { value: "ocorrencias", label: "Ocorrências / Histórico" },
                    { value: "contratos", label: "Contratos / Títulos" },
                    { value: "boletos", label: "Boletos Emitidos" }
                ].find(o => o.value === val.trim());
                return opt || { value: val.trim(), label: val.trim() };
            });
            setSelectedFontes(loadedFontesList);

            setStartDate(config.startDate || "");
            setEndDate(config.endDate || "");
            setSelectedOperators(config.selectedOperators || []);
            setSelectedStatus(config.selectedStatus || []);
            setSelectedCarteiras(config.selectedCarteiras || []);
            setSelectedSituations(config.selectedSituations || []);
            setSelectedTipoOcorrencias(config.selectedTipoOcorrencias || []);
            setSelectedClientes(config.selectedClientes || []);
            if (config.excetoDevolvidos !== undefined) {
                setExcetoDevolvidos(config.excetoDevolvidos);
            } else {
                setExcetoDevolvidos(false);
            }
            setDateFormat(config.dateFormat || "dd/MM/yyyy");
            setValueFormat(config.valueFormat || "R$ #,##0.00");
            setGroupColumn(config.groupColumn || "");
            setAggregateColumn(config.aggregateColumn || "");
            setAggregateType(config.aggregateType || "count");
            setChartType(config.chartType || "bar");
            setColorPalette(config.colorPalette || "material");
            setColumnAliases(config.columnAliases || {});

            const unionCols = [];
            const seen = new Set();
            loadedFontesList.forEach(f => {
                const list = columnsByFonte[f.value] || [];
                list.forEach(col => {
                    if (!seen.has(col.value)) {
                        seen.add(col.value);
                        unionCols.push(col);
                    }
                });
            });

            if (config.selectedColumns) {
                const mappedCols = config.selectedColumns.map(val => {
                    if (typeof val === "object" && val !== null) return val;
                    const opt = unionCols.find(c => c.value === val);
                    return opt || { value: val, label: val };
                });
                setSelectedColumns(mappedCols);
            } else {
                setSelectedColumns(unionCols);
            }

            setActiveReport(report);
            setNewReportName(report.ccNome);
            successAlert(`Relatório "${report.ccNome}" carregado.`);
        } catch (e) {
            errorAlert("Erro ao analisar configuração salva.");
        }
    };

    // Delete saved configuration
    const handleDeleteReport = (id, e) => {
        e.stopPropagation();
        postNoResponse(`/reports/saved/${id}`, {}, { method: "DELETE" })
            .then(() => {
                successAlert("Relatório excluído.");
                if (activeReport?.cvId === id) {
                    setActiveReport(null);
                    setNewReportName("");
                }
                loadSavedReports();
            })
            .catch(() => errorAlert("Erro ao excluir relatório."));
    };

    const paletteDefinitions = useMemo(() => ({
        // Paletas Multicolor
        material: {
            label: "🎨 Multicolor Material (Padrão)",
            themeName: "info",
            colors: ["#1A73E8", "#4CAF50", "#9C27B0", "#FF9800", "#F44336", "#344767", "#7b809a"]
        },
        sunset_orange: {
            label: "🌅 Multicolor Sunset (Laranja/Roxo)",
            themeName: "warning",
            colors: ["#E91E63", "#D81B60", "#8E24AA", "#5E35B1", "#3949AB", "#FF5722", "#FF9800"]
        },
        neon_pastel: {
            label: "🦄 Multicolor Pastel",
            themeName: "secondary",
            colors: ["#4E73DF", "#1CC88A", "#36B9CC", "#F6C23E", "#E74A3B", "#858796", "#F8F9FC"]
        },
        gold_luxury: {
            label: "👑 Multicolor Dourado & Âmbar",
            themeName: "warning",
            colors: ["#795548", "#8D6E63", "#BCAAA4", "#FF8F00", "#FFA000", "#FFB300", "#FFC107"]
        },

        // Paletas Monocromáticas (Estilo MS Excel)
        mono_blue: {
            label: "🟦 Monocromático Azul",
            themeName: "info",
            colors: ["#0A3663", "#154C79", "#1E6091", "#2E86AB", "#52B2CF", "#89D2DC", "#BEE7E8"]
        },
        mono_orange: {
            label: "🟧 Monocromático Laranja",
            themeName: "warning",
            colors: ["#9E2A2B", "#B83B2F", "#D35400", "#E67E22", "#F39C12", "#F8C471", "#FADBD8"]
        },
        mono_green: {
            label: "🟩 Monocromático Verde",
            themeName: "success",
            colors: ["#1E4620", "#2D6A4F", "#40916C", "#52B788", "#74C69D", "#95D5B2", "#B7E4C7"]
        },
        mono_purple: {
            label: "🟪 Monocromático Roxo",
            themeName: "secondary",
            colors: ["#3A0CA3", "#4361EE", "#4895EF", "#560BAD", "#7209B7", "#B5179E", "#E0AAFF"]
        },
        mono_terracotta: {
            label: "🟫 Monocromático Terracota",
            themeName: "warning",
            colors: ["#4A2E2B", "#6F3E37", "#9E5246", "#C46D5E", "#DC8D7E", "#E6A598", "#F2B8AC"]
        },
        monochrome_dark: {
            label: "⬛ Monocromático Grafite",
            themeName: "dark",
            colors: ["#111827", "#1F2937", "#374151", "#4B5563", "#6B7280", "#9CA3AF", "#D1D5DB"]
        }
    }), []);

    // Compute interactive chart datasets and labels dynamically from local tableData
    const chartData = useMemo(() => {
        if (!tableData || tableData.length === 0 || !groupColumn) return null;

        // Group rows
        const groups = {};
        tableData.forEach(row => {
            let val = row[groupColumn];
            
            if (val !== null && val !== undefined && val !== "") {
                const lowerGroupCol = groupColumn.toLowerCase();
                if (lowerGroupCol.includes("data") || lowerGroupCol.includes("criacao") || lowerGroupCol.includes("vencimento") || lowerGroupCol.includes("pagamento")) {
                    val = formatDateValue(val, dateFormat);
                } else if (lowerGroupCol.includes("valor") || lowerGroupCol.includes("desconto") || lowerGroupCol.includes("acrescimo")) {
                    val = formatNumericValue(val, valueFormat);
                }
            }

            const key = val === null || val === undefined || val === "" ? "Não Informado" : val.toString();
            if (!groups[key]) groups[key] = [];
            groups[key].push(row);
        });

        const labels = [];
        const values = [];

        Object.entries(groups).forEach(([key, rows]) => {
            labels.push(key);
            if (aggregateType === "sum") {
                const totalSum = rows.reduce((sum, r) => {
                    const num = Number(r[aggregateColumn]);
                    return sum + (isNaN(num) ? 0 : num);
                }, 0);
                values.push(Number(totalSum.toFixed(2)));
            } else {
                values.push(rows.length);
            }
        });

        const currentPalette = paletteDefinitions[colorPalette] || paletteDefinitions.material;
        const bgColors = currentPalette.colors;
        let repeatedBgColors = [];
        for (let i = 0; i < values.length; i++) {
            repeatedBgColors.push(bgColors[i % bgColors.length]);
        }

        if (chartType === "pie" || chartType === "pie3d" || chartType === "doughnut") {
            return {
                labels,
                datasets: {
                    label: "Total",
                    backgroundColors: repeatedBgColors,
                    data: values
                }
            };
        } else {
            return {
                labels,
                datasets: [
                    {
                        label: aggregateType === "sum" ? `Soma de ${aggregateColumn}` : "Total de Registros",
                        backgroundColors: repeatedBgColors,
                        color: currentPalette.themeName,
                        data: values
                    }
                ]
            };
        }
    }, [tableData, groupColumn, aggregateColumn, aggregateType, chartType, colorPalette, paletteDefinitions, dateFormat, valueFormat]);

    // Available columns for grouping dropdown
    const groupableColumns = useMemo(() => {
        if (tableData.length === 0) return [];
        return Object.keys(tableData[0]).filter(k => k !== "ID");
    }, [tableData]);

    // Numeric columns for sum aggregation
    const numericColumns = useMemo(() => {
        if (tableData.length === 0) return [];
        const firstRow = tableData[0];
        return Object.keys(firstRow).filter(k => {
            const val = firstRow[k];
            return typeof val === "number" || k.toLowerCase().includes("valor") || k.toLowerCase().includes("desconto");
        });
    }, [tableData]);

    return (
        <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} md={12}>
                {/* Header Controls Card */}
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? "#292C37" : "#fafafa",
                        borderColor: darkMode ? "#333" : "#ddd",
                    }}
                >
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                        <MDBox>
                            <MDTypography variant="h5" color={darkMode ? "white" : "dark"}>
                                Relatórios Dinâmicos
                            </MDTypography>
                            {activeReport && (
                                <MDTypography variant="caption" color="info" fontWeight="bold">
                                    Editando: {activeReport.ccNome}
                                </MDTypography>
                            )}
                        </MDBox>
                        <MDBox display="flex" gap={1}>
                            <MDButton variant="contained" color="success" size="small" onClick={() => setSaveDialogOpen(true)} disabled={selectedFontes.length === 0}>
                                <Icon sx={{ mr: 1 }}>save</Icon> Salvar Configuração
                            </MDButton>
                        </MDBox>
                    </MDBox>

                    {/* Query Filter Area */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                            <Grid container spacing={1.5}>
                                <Grid item xs={12} sm={4}>
                                    <MDBox display="flex" alignItems="flex-end" gap={1}>
                                        <MDBox sx={{ flex: 1 }}>
                                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                                Relatório Salvo
                                            </MDTypography>
                                            <MDSelect
                                                isMulti={false}
                                                name="savedReportsSelect"
                                                placeholder="Selecione um relatório..."
                                                value={activeReport ? { value: activeReport.cvId, label: activeReport.ccNome, report: activeReport } : null}
                                                options={savedReports.map((report) => ({
                                                    value: report.cvId,
                                                    label: report.ccNome,
                                                    report: report
                                                }))}
                                                onChange={(opt) => {
                                                    if (opt) {
                                                        handleLoadReportConfig(opt.report);
                                                    } else {
                                                        handleClearFilters();
                                                    }
                                                }}
                                            />
                                        </MDBox>
                                        {activeReport && (
                                            <IconButton
                                                color="error"
                                                onClick={(e) => handleDeleteReport(activeReport.cvId, e)}
                                                sx={{ mb: 0.2, p: 0.8 }}
                                                title="Excluir Relatório Salvo"
                                            >
                                                <Icon fontSize="medium">delete</Icon>
                                            </IconButton>
                                        )}
                                    </MDBox>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                        Fonte de Dados
                                    </MDTypography>
                                    <MDSelect
                                        isMulti={true}
                                        name="selectedFontes"
                                        placeholder="Selecione as fontes..."
                                        value={selectedFontes}
                                        options={[
                                            { value: "acordos", label: "Acordos Formalizados" },
                                            { value: "devedores", label: "Fichas / Devedores" },
                                            { value: "ocorrencias", label: "Ocorrências / Histórico" },
                                            { value: "contratos", label: "Contratos / Títulos" },
                                            { value: "boletos", label: "Boletos Emitidos" },
                                            ...customReportsList.map(r => ({ value: `custom_${r.cvId}`, label: `⭐ ${r.ccNome}` }))
                                        ]}
                                        onChange={(opt) => {
                                            const newFontes = opt || [];
                                            setSelectedFontes(newFontes);
                                            
                                            // Compute union of columns for these new sources
                                            const cols = [];
                                            const seen = new Set();
                                            newFontes.forEach(f => {
                                                const list = columnsByFonte[f.value] || [];
                                                list.forEach(col => {
                                                    if (!seen.has(col.value)) {
                                                        seen.add(col.value);
                                                        cols.push(col);
                                                    }
                                                });
                                            });
                                            setSelectedColumns(cols);
                                            setTableData([]);
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                        Data Inicial
                                    </MDTypography>
                                    <MDTextFieldCalendar
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        darkMode={darkMode}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                        Data Final
                                    </MDTypography>
                                    <MDTextFieldCalendar
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        darkMode={darkMode}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                        Operadores
                                    </MDTypography>
                                    <MDSelect
                                        isMulti={true}
                                        name="operators"
                                        placeholder="Todos"
                                        value={selectedOperators}
                                        options={operatorsList}
                                        getOptionValue={(option) => option.cvCodUsuario || option.id}
                                        getOptionLabel={(option) => option.ccNome || option.nome || ""}
                                        onChange={(val) => setSelectedOperators(val || [])}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                        Clientes
                                    </MDTypography>
                                    <MDSelect
                                        isMulti={true}
                                        name="clientes"
                                        placeholder="Todos"
                                        value={selectedClientes}
                                        options={clientesList}
                                        getOptionValue={(option) => option.id}
                                        getOptionLabel={(option) => {
                                            return option.nomeComum || option.nomeCompleto || option.nome || "";
                                        }}
                                        onChange={(val) => setSelectedClientes(val || [])}
                                    />
                                </Grid>

                                {/* Conditional filters based on selected data sources */}
                                {fonteDados.includes("devedores") && (
                                    <>
                                        <Grid item xs={12} sm={4}>
                                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                                Status Ficha
                                            </MDTypography>
                                            <MDSelect
                                                isMulti={true}
                                                name="status"
                                                placeholder="Todos"
                                                value={selectedStatus}
                                                options={statusList}
                                                getOptionValue={(option) => option.cvId || option.id}
                                                getOptionLabel={(option) => option.ccNome || option.nome || ""}
                                                onChange={(val) => setSelectedStatus(val || [])}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                                Carteiras
                                            </MDTypography>
                                            <MDSelect
                                                isMulti={true}
                                                name="carteiras"
                                                placeholder="Todos"
                                                value={selectedCarteiras}
                                                options={carteirasList}
                                                getOptionValue={(option) => option.cvId || option.id}
                                                getOptionLabel={(option) => option.ccNome || option.nome || ""}
                                                onChange={(val) => setSelectedCarteiras(val || [])}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {fonteDados.includes("acordos") && (
                                    <Grid item xs={12} sm={4}>
                                        <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                            Situações
                                        </MDTypography>
                                        <MDSelect
                                            isMulti={true}
                                            name="situations"
                                            placeholder="Todos"
                                            value={selectedSituations}
                                            options={situationsList}
                                            getOptionValue={(option) => option.id}
                                            getOptionLabel={(option) => option.name}
                                            onChange={(val) => setSelectedSituations(val || [])}
                                        />
                                    </Grid>
                                )}

                                {fonteDados.includes("ocorrencias") && (
                                    <Grid item xs={12} sm={4}>
                                        <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                            Tipos de Ocorrência
                                        </MDTypography>
                                        <MDSelect
                                            isMulti={true}
                                            name="tipoOcorrencias"
                                            placeholder="Todos"
                                            value={selectedTipoOcorrencias}
                                            options={tipoOcorrenciasList}
                                            getOptionValue={(option) => option.cvId || option.id}
                                            getOptionLabel={(option) => option.ccNome || option.nome || ""}
                                            onChange={(val) => setSelectedTipoOcorrencias(val || [])}
                                        />
                                    </Grid>
                                )}

                                {/* Data Formats Dropdowns */}
                                <Grid item xs={12} sm={4}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                        Formato Data
                                    </MDTypography>
                                    <MDSelect
                                        isMulti={false}
                                        name="dateFormat"
                                        value={[
                                            { value: "dd/MM/yyyy", label: "DD/MM/YYYY" },
                                            { value: "dd/MM/yyyy HH:mm:ss", label: "DD/MM/YYYY HH:MM:SS" },
                                            { value: "yyyy-MM-dd", label: "YYYY-MM-DD" }
                                        ].find(o => o.value === dateFormat) || null}
                                        options={[
                                            { value: "dd/MM/yyyy", label: "DD/MM/YYYY" },
                                            { value: "dd/MM/yyyy HH:mm:ss", label: "DD/MM/YYYY HH:MM:SS" },
                                            { value: "yyyy-MM-dd", label: "YYYY-MM-DD" }
                                        ]}
                                        onChange={(opt) => setDateFormat(opt?.value || "dd/MM/yyyy")}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.3, display: "block" }}>
                                        Formato Valor
                                    </MDTypography>
                                    <MDSelect
                                        isMulti={false}
                                        name="valueFormat"
                                        value={[
                                            { value: "R$ #,##0.00", label: "R$ #.##0,00" },
                                            { value: "#,##0.00", label: "#.##0,00 (Decimal)" },
                                            { value: "#,##0", label: "#.##0 (Inteiro)" }
                                        ].find(o => o.value === valueFormat) || null}
                                        options={[
                                            { value: "R$ #,##0.00", label: "R$ #.##0,00" },
                                            { value: "#,##0.00", label: "#.##0,00 (Decimal)" },
                                            { value: "#,##0", label: "#.##0 (Inteiro)" }
                                        ]}
                                        onChange={(opt) => setValueFormat(opt?.value || "R$ #,##0.00")}
                                    />
                                </Grid>
                                
                                {fonteDados.includes("devedores") && (
                                    <Grid item xs={12} sm={4} display="flex" alignItems="center">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={excetoDevolvidos}
                                                    onChange={(e) => setExcetoDevolvidos(e.target.checked)}
                                                />
                                            }
                                            label={<MDTypography variant="caption" fontWeight="bold" color="text">Exceto Devolvidos</MDTypography>}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        {/* Campos do Relatório Box Select */}
                        {!fonteDados.includes("custom_") && (
                        <Grid item xs={12} sm={4}>
                            <MDBox
                                sx={{
                                    p: 1.5,
                                    height: "100%",
                                    minHeight: "230px",
                                    maxHeight: "230px",
                                    display: "flex",
                                    flexDirection: "column",
                                    background: darkMode ? "#1d1d35" : "#fafafa",
                                    border: `1px solid ${darkMode ? "#333" : "#ddd"}`,
                                    borderRadius: "8px",
                                }}
                            >
                                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1} pb={0.5} sx={{ borderBottom: darkMode ? "1px solid #333" : "1px solid #eee" }}>
                                    <MDTypography variant="caption" fontWeight="bold" color="text">
                                        Campos do Relatório ({selectedColumns.length}/{availableColumns.length})
                                    </MDTypography>
                                    <MDBox display="flex" gap={1}>
                                        <MDButton
                                            variant="text"
                                            color="info"
                                            size="small"
                                            sx={{ p: 0.2, minWidth: 0, minHeight: 0, fontSize: "0.7rem" }}
                                            onClick={() => setSelectedColumns(availableColumns)}
                                        >
                                            Todos
                                        </MDButton>
                                        <MDButton
                                            variant="text"
                                            color="secondary"
                                            size="small"
                                            sx={{ p: 0.2, minWidth: 0, minHeight: 0, fontSize: "0.7rem" }}
                                            onClick={() => setSelectedColumns([])}
                                        >
                                            Limpar
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                                <MDBox
                                    sx={{
                                        overflowY: "auto",
                                        flex: 1,
                                        pr: 0.5,
                                        "::-webkit-scrollbar": { width: "4px" },
                                        "::-webkit-scrollbar-thumb": {
                                            background: darkMode ? "#444" : "#ccc",
                                            borderRadius: "2px",
                                        },
                                    }}
                                >
                                    <Grid container spacing={0.25}>
                                        {availableColumns.map((col) => {
                                            const isChecked = selectedColumns.some((c) => c.value === col.value);
                                            return (
                                                <Grid item xs={12} key={col.value} display="flex" alignItems="center" sx={{ py: 0.05 }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={isChecked}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedColumns([...selectedColumns, col]);
                                                            } else {
                                                                setSelectedColumns(selectedColumns.filter((c) => c.value !== col.value));
                                                            }
                                                        }}
                                                        sx={{ p: 0.2, transform: "scale(0.8)" }}
                                                    />
                                                    <MDTypography
                                                        variant="caption"
                                                        color={darkMode ? "white" : "dark"}
                                                        onClick={() => {
                                                            if (isChecked) {
                                                                setSelectedColumns(selectedColumns.filter((c) => c.value !== col.value));
                                                            } else {
                                                                setSelectedColumns([...selectedColumns, col]);
                                                            }
                                                        }}
                                                        sx={{ cursor: "pointer", select: "none", fontSize: "0.75rem", ml: 0.2 }}
                                                    >
                                                        {col.label}
                                                    </MDTypography>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </MDBox>
                            </MDBox>
                        </Grid>
                        )}

                        {selectedColumns.length > 0 && !fonteDados.includes("custom_") && (
                            <Grid item xs={12}>
                                <MDBox
                                    sx={{
                                        p: 1.5,
                                        mt: 1,
                                        background: darkMode ? "#1d1d35" : "#fafafa",
                                        border: `1px solid ${darkMode ? "#333" : "#ddd"}`,
                                        borderRadius: "8px",
                                    }}
                                >
                                    <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ display: "block", mb: 0.5 }}>
                                        Ordem das Colunas e Apelidos (Aliases) para Exportação
                                    </MDTypography>
                                    <MDBox
                                        sx={{
                                            maxHeight: "150px",
                                            overflowY: "auto",
                                            pr: 1,
                                            "::-webkit-scrollbar": { width: "4px" },
                                            "::-webkit-scrollbar-thumb": {
                                                background: darkMode ? "#444" : "#ccc",
                                                borderRadius: "2px",
                                            },
                                        }}
                                    >
                                        <Grid container spacing={1}>
                                            {selectedColumns.map((col, idx) => (
                                                <Grid item xs={12} sm={6} md={4} key={col.value}>
                                                    <MDBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        sx={{
                                                            p: 0.5,
                                                            border: `1px solid ${darkMode ? "#333" : "#eee"}`,
                                                            borderRadius: "4px",
                                                            background: darkMode ? "#242442" : "#fff",
                                                        }}
                                                    >
                                                        <MDBox display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
                                                            <MDBox display="flex" flexDirection="row">
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={idx === 0}
                                                                    onClick={() => {
                                                                        const newCols = [...selectedColumns];
                                                                        const temp = newCols[idx - 1];
                                                                        newCols[idx - 1] = newCols[idx];
                                                                        newCols[idx] = temp;
                                                                        setSelectedColumns(newCols);
                                                                    }}
                                                                    sx={{ p: 0.2, color: darkMode ? "white" : "#555" }}
                                                                >
                                                                    <Icon sx={{ fontSize: "1rem !important" }}>keyboard_arrow_up</Icon>
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={idx === selectedColumns.length - 1}
                                                                    onClick={() => {
                                                                        const newCols = [...selectedColumns];
                                                                        const temp = newCols[idx + 1];
                                                                        newCols[idx + 1] = newCols[idx];
                                                                        newCols[idx] = temp;
                                                                        setSelectedColumns(newCols);
                                                                    }}
                                                                    sx={{ p: 0.2, color: darkMode ? "white" : "#555" }}
                                                                >
                                                                    <Icon sx={{ fontSize: "1rem !important" }}>keyboard_arrow_down</Icon>
                                                                </IconButton>
                                                            </MDBox>
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                color={darkMode ? "white" : "dark"}
                                                                sx={{
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    maxWidth: "100px",
                                                                    fontSize: "0.7rem"
                                                                }}
                                                                title={col.label}
                                                            >
                                                                {col.label}
                                                            </MDTypography>
                                                        </MDBox>
                                                        <MDBox sx={{ width: "90px" }}>
                                                            <TextField
                                                                placeholder="Apelido..."
                                                                variant="outlined"
                                                                size="small"
                                                                value={columnAliases[col.value] || ""}
                                                                onChange={(e) => {
                                                                    setColumnAliases({
                                                                        ...columnAliases,
                                                                        [col.value]: e.target.value
                                                                    });
                                                                }}
                                                                inputProps={{
                                                                    style: {
                                                                        fontSize: "0.7rem",
                                                                        padding: "2px 4px",
                                                                        height: "auto",
                                                                        background: darkMode ? "#1d1d35" : "#fff",
                                                                        color: darkMode ? "#fff" : "#000",
                                                                        border: "none"
                                                                    }
                                                                }}
                                                            />
                                                        </MDBox>
                                                    </MDBox>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        )}

                        {/* Action Buttons */}
                        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1.5} pt={1.5}>
                            <MDButton size="small" variant="outlined" color="secondary" onClick={handleClearFilters} disabled={loadingTable}>
                                <Icon sx={{ mr: 1 }}>clear_all</Icon> Limpar Filtros
                            </MDButton>
                            <MDButton size="small" variant="contained" color="info" onClick={handleRunReport} disabled={loadingTable}>
                                {loadingTable ? <CircularProgress size={16} color="inherit" /> : <><Icon sx={{ mr: 1 }}>search</Icon> Filtrar</>}
                            </MDButton>
                            <MDButton size="small" variant="contained" color="warning" onClick={handleExportXlsx} disabled={tableData.length === 0}>
                                <Icon sx={{ mr: 1 }}>download</Icon> Exportar XLSX
                            </MDButton>
                        </Grid>
                    </Grid>
                </Card>

                {/* Chart Controls & Visualizer */}
                {tableData.length > 0 && (
                    <Card
                        variant="outlined"
                        sx={{
                            p: 2,
                            mb: 2,
                            background: darkMode ? "#292C37" : "#fff",
                            borderColor: darkMode ? "#333" : "#ddd",
                        }}
                    >
                        <MDTypography variant="h6" color={darkMode ? "white" : "dark"} mb={2}>
                            Gráficos e Agrupamentos Interativos
                        </MDTypography>
                        <Grid container spacing={2} alignItems="center" mb={3}>
                            <Grid item xs={12} md={aggregateType === "sum" ? 2.2 : 2.5}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Agrupar por</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    name="groupColumn"
                                    value={groupableColumns.map(col => ({ value: col, label: col })).find(o => o.value === groupColumn) || null}
                                    options={groupableColumns.map(col => ({ value: col, label: col }))}
                                    onChange={(opt) => setGroupColumn(opt?.value || "")}
                                />
                            </Grid>

                            <Grid item xs={12} md={aggregateType === "sum" ? 1.8 : 2.2}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Métrica</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    name="aggregateType"
                                    value={[
                                        { value: "count", label: "Contagem de Registros" },
                                        { value: "sum", label: "Soma de Valor" }
                                    ].find(o => o.value === aggregateType) || null}
                                    options={[
                                        { value: "count", label: "Contagem de Registros" },
                                        { value: "sum", label: "Soma de Valor" }
                                    ].filter(o => o.value !== "sum" || numericColumns.length > 0)}
                                    onChange={(opt) => setAggregateType(opt?.value || "count")}
                                />
                            </Grid>

                            {aggregateType === "sum" && (
                                <Grid item xs={12} md={2}>
                                    <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Coluna Métrica</InputLabel>
                                    <MDSelect
                                        isMulti={false}
                                        name="aggregateColumn"
                                        value={numericColumns.map(col => ({ value: col, label: col })).find(o => o.value === aggregateColumn) || null}
                                        options={numericColumns.map(col => ({ value: col, label: col }))}
                                        onChange={(opt) => setAggregateColumn(opt?.value || "")}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} md={aggregateType === "sum" ? 2 : 2.5}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo de Gráfico</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    name="chartCategory"
                                    value={[
                                        { value: "bar_vert", label: "📊 Colunas Verticais" },
                                        { value: "bar_horiz", label: "▬ Barras Horizontais" },
                                        { value: "pie_doughnut", label: "🥧 Pizza & Rosca" },
                                        { value: "lines", label: "📈 Linhas" }
                                    ].find(o => o.value === ((type) => {
                                        if (type === "bar" || type === "bar3d") return "bar_vert";
                                        if (type === "hbar" || type === "hbar3d") return "bar_horiz";
                                        if (type === "pie" || type === "pie3d" || type === "doughnut") return "pie_doughnut";
                                        if (type === "line") return "lines";
                                        return "bar_vert";
                                    })(chartType)) || null}
                                    options={[
                                        { value: "bar_vert", label: "📊 Colunas Verticais" },
                                        { value: "bar_horiz", label: "▬ Barras Horizontais" },
                                        { value: "pie_doughnut", label: "🥧 Pizza & Rosca" },
                                        { value: "lines", label: "📈 Linhas" }
                                    ]}
                                    onChange={(opt) => {
                                        const cat = opt?.value || "bar_vert";
                                        if (cat === "bar_vert") setChartType("bar");
                                        else if (cat === "bar_horiz") setChartType("hbar");
                                        else if (cat === "pie_doughnut") setChartType("pie");
                                        else if (cat === "lines") setChartType("line");
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={aggregateType === "sum" ? 2 : 2.5}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Modelo do Gráfico</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    name="chartType"
                                    value={((cat) => {
                                        switch (cat) {
                                            case "bar_vert":
                                                return [
                                                    { value: "bar", label: "Colunas Verticais 2D" },
                                                    { value: "bar3d", label: "Colunas Verticais 3D" }
                                                ];
                                            case "bar_horiz":
                                                return [
                                                    { value: "hbar", label: "Barras Horizontais 2D" },
                                                    { value: "hbar3d", label: "Barras Horizontais 3D" }
                                                ];
                                            case "pie_doughnut":
                                                return [
                                                    { value: "pie", label: "Pizza 2D" },
                                                    { value: "pie3d", label: "Pizza 3D" },
                                                    { value: "doughnut", label: "Rosca / Anel 2D" }
                                                ];
                                            case "lines":
                                                return [
                                                    { value: "line", label: "Linhas 2D" }
                                                ];
                                            default:
                                                return [{ value: "bar", label: "Colunas Verticais 2D" }];
                                        }
                                    })(((type) => {
                                        if (type === "bar" || type === "bar3d") return "bar_vert";
                                        if (type === "hbar" || type === "hbar3d") return "bar_horiz";
                                        if (type === "pie" || type === "pie3d" || type === "doughnut") return "pie_doughnut";
                                        if (type === "line") return "lines";
                                        return "bar_vert";
                                    })(chartType)).find(o => o.value === chartType) || null}
                                    options={((cat) => {
                                        switch (cat) {
                                            case "bar_vert":
                                                return [
                                                    { value: "bar", label: "Colunas Verticais 2D" },
                                                    { value: "bar3d", label: "Colunas Verticais 3D" }
                                                ];
                                            case "bar_horiz":
                                                return [
                                                    { value: "hbar", label: "Barras Horizontais 2D" },
                                                    { value: "hbar3d", label: "Barras Horizontais 3D" }
                                                ];
                                            case "pie_doughnut":
                                                return [
                                                    { value: "pie", label: "Pizza 2D" },
                                                    { value: "pie3d", label: "Pizza 3D" },
                                                    { value: "doughnut", label: "Rosca / Anel 2D" }
                                                ];
                                            case "lines":
                                                return [
                                                    { value: "line", label: "Linhas 2D" }
                                                ];
                                            default:
                                                return [{ value: "bar", label: "Colunas Verticais 2D" }];
                                        }
                                    })(((type) => {
                                        if (type === "bar" || type === "bar3d") return "bar_vert";
                                        if (type === "hbar" || type === "hbar3d") return "bar_horiz";
                                        if (type === "pie" || type === "pie3d" || type === "doughnut") return "pie_doughnut";
                                        if (type === "line") return "lines";
                                        return "bar_vert";
                                    })(chartType))}
                                    onChange={(opt) => setChartType(opt?.value || "bar")}
                                />
                            </Grid>

                            <Grid item xs={12} md={aggregateType === "sum" ? 2 : 2.3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Paleta de Cores</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    name="colorPalette"
                                    value={Object.entries(paletteDefinitions).map(([key, def]) => ({ value: key, label: def.label })).find(o => o.value === colorPalette) || null}
                                    options={Object.entries(paletteDefinitions).map(([key, def]) => ({ value: key, label: def.label }))}
                                    onChange={(opt) => setColorPalette(opt?.value || "material")}
                                />
                            </Grid>
                        </Grid>

                        {chartData ? (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={chartData.labels && chartData.labels.length > 10 ? 12 : 8}>
                                    <MDBox 
                                        width="100%" 
                                        height="22rem" 
                                        display="flex" 
                                        justifyContent="center" 
                                        alignItems="center"
                                        sx={chartType.endsWith("3d") ? {
                                            filter: "drop-shadow(0px 14px 12px rgba(0,0,0,0.35))",
                                            transition: "all 0.3s ease-in-out",
                                            "&:hover": {
                                                filter: "drop-shadow(0px 18px 16px rgba(0,0,0,0.45))",
                                                transform: "translateY(-3px)"
                                            }
                                        } : {
                                            transition: "all 0.3s ease"
                                        }}
                                    >
                                        {(chartType === "pie" || chartType === "pie3d") ? (
                                            <PieChart chart={chartData} height="18rem" />
                                        ) : chartType === "doughnut" ? (
                                            <DefaultDoughnutChart chart={chartData} height="18rem" />
                                        ) : (chartType === "hbar" || chartType === "hbar3d") ? (
                                            <HorizontalBarChart chart={chartData} height="18rem" />
                                        ) : chartType === "line" ? (
                                            <DefaultLineChart chart={chartData} height="18rem" />
                                        ) : (
                                            <VerticalBarChart chart={chartData} height="18rem" />
                                        )}
                                    </MDBox>
                                </Grid>
                                <Grid item xs={12} md={chartData.labels && chartData.labels.length > 10 ? 12 : 4}>
                                    <MDBox sx={{ 
                                        height: chartData.labels && chartData.labels.length > 10 ? "auto" : "22rem",
                                        maxHeight: "22rem",
                                        overflowY: "auto",
                                        '& .MuiTableContainer-root': { boxShadow: 'none' },
                                        pr: 1,
                                        "::-webkit-scrollbar": { width: "6px" },
                                        "::-webkit-scrollbar-thumb": { background: darkMode ? "#444" : "#ccc", borderRadius: "3px" }
                                    }}>
                                        <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'transparent' }}>
                                            <Table size="small" stickyHeader sx={{ '& td, & th': { borderBottom: '1px solid #f0f2f5' } }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell width="70%" sx={{ color: darkMode ? "#fff" : "inherit", fontWeight: "bold", fontSize: 13, backgroundColor: darkMode ? "#1a2035" : "#fff" }}>{groupColumn || "Categoria"}</TableCell>
                                                        <TableCell width="30%" align="right" sx={{ color: darkMode ? "#fff" : "inherit", fontWeight: "bold", fontSize: 13, backgroundColor: darkMode ? "#1a2035" : "#fff" }}>{aggregateType === "sum" ? `Soma (${aggregateColumn})` : "Total"}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(chartData.labels || []).map((label, idx) => {
                                                        const dataArr = Array.isArray(chartData.datasets) ? chartData.datasets[0]?.data : chartData.datasets?.data;
                                                        const bgArr = Array.isArray(chartData.datasets) ? chartData.datasets[0]?.backgroundColors : chartData.datasets?.backgroundColors;
                                                        let val = (dataArr || [])[idx] || 0;
                                                        let cName = (bgArr || [])[idx] || "#1A73E8";
                                                        let formattedVal = val;
                                                        if (aggregateType === "sum") {
                                                            formattedVal = Number(val).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
                                                        }
                                                        return (
                                                            <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                                <TableCell component="th" scope="row" sx={{ color: darkMode ? "#bbb" : "inherit", fontSize: 13, py: 1 }}>
                                                                    <MDBox display="inline-block" width="12px" height="12px" borderRadius="50%" sx={{ backgroundColor: cName }} mr={1} />
                                                                    {label}
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ color: darkMode ? "#bbb" : "inherit", fontSize: 13, py: 1 }}>
                                                                    {formattedVal}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </MDBox>
                                </Grid>
                            </Grid>
                        ) : (
                            <MDBox width="100%" height="22rem" display="flex" justifyContent="center" alignItems="center">
                                <MDTypography variant="button" color="secondary">
                                    Configure as chaves acima para visualizar o gráfico.
                                </MDTypography>
                            </MDBox>
                        )}
                    </Card>
                )}

                {/* Data Results Table */}
                <Card
                    variant="outlined"
                    sx={{
                        p: 2,
                        background: darkMode ? "#292C37" : "#fff",
                        borderColor: darkMode ? "#333" : "#ddd",
                    }}
                >
                    <MDBox p={1} pb={2} display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>
                            Tabela de Dados
                        </MDTypography>
                        {tableData.length > 0 && (
                            <MDTypography variant="button" color="secondary">
                                Total de registros: {tableData.length}
                            </MDTypography>
                        )}
                    </MDBox>
                    {tableData.length > 0 ? (
                        <DataTable
                            loading={loadingTable}
                            columnData={columns}
                            data={displayedData}
                            fetchData={handleFetchData}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            tableState={tableState}
                            changeFilter={aplicar}
                        />
                    ) : (
                        <MDBox p={4} textAlign="center">
                            <MDTypography variant="button" color="secondary">
                                Nenhum dado carregado. Utilize o painel de filtros para realizar uma busca.
                            </MDTypography>
                        </MDBox>
                    )}
                </Card>
            </Grid>



            {/* Save Dialog */}
            <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
                <DialogTitle>Salvar Configuração do Relatório</DialogTitle>
                <DialogContent sx={{ minWidth: "350px", pt: 1 }}>
                    <MDInput
                        autoFocus
                        label="Nome do Relatório"
                        type="text"
                        fullWidth
                        value={newReportName}
                        onChange={(e) => setNewReportName(e.target.value)}
                        variant="outlined"
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setSaveDialogOpen(false)} color="secondary" variant="outlined" sx={{ mr: 1 }}>
                        Cancelar
                    </MDButton>
                    {activeReport ? (
                        <>
                            <MDButton onClick={() => handleSaveReport(null)} color="info" variant="contained" sx={{ mr: 1 }}>
                                Salvar como Novo
                            </MDButton>
                            <MDButton onClick={() => handleSaveReport(activeReport.cvId)} color="success" variant="contained">
                                Salvar Alterações
                            </MDButton>
                        </>
                    ) : (
                        <MDButton onClick={() => handleSaveReport(null)} color="info" variant="contained">
                            Salvar
                        </MDButton>
                    )}
                </DialogActions>
            </Dialog>
        </Grid>
    );
}
